import { Rank } from '@domain/insights/models/Rank';
import { WeeklySearchVolume } from '@domain/insights/models/SearchTrend';
import { Search } from '@domain/insights/models/Search';
import { ApiClient } from '../../ApiClient';
import { InsightsMapper } from '../mappers/InsightsMapper';
import { RankDTO, WeeklySearchVolumeDTO, SearchDTO } from '../dto/InsightsDTO';
import { PaginatedDTO } from '../../paginated.dto';
import { PaginationRequest } from '@domain/base/Resource';

export class InsightsDataSource {
  constructor(private api: ApiClient) { }

  async getRanksByProductId(productId: string): Promise<Rank[]> {
    try {
      const response = await this.api.get<RankDTO[]>(`/insights/ranks/product/${productId}`);
      return response.data.map(InsightsMapper.rankToModel);
    } catch (error) {
      throw new Error('Failed to fetch ranks');
    }
  }

  async getRanksBySearchTerm(searchTerm: string): Promise<RankDTO[]> {
    try {
      const response = await this.api.get<RankDTO[]>(`/insights/ranks/search-term/${searchTerm}`);
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch ranks');
    }
  }

  async updateRankSettings(
    productId: string, 
    searchTerm: string, 
    useForForecasting?: boolean, 
    excludeFromForecasting?: boolean
  ): Promise<Rank> {
    try {
      const data = {
        productId,
        searchTerm,
        useForForecasting,
        excludeFromForecasting
      };
      const response = await this.api.patch<RankDTO>('/insights/ranks', data);
      return InsightsMapper.rankToModel(response.data);
    } catch (error) {
      throw new Error('Failed to update rank settings');
    }
  }

  async getSearchVolume(searchTerm: string, countryCode: string): Promise<WeeklySearchVolume[]> {
    try {
      const response = await this.api.get<WeeklySearchVolumeDTO[]>(`/insights/weekly-search-volume?searchTerm=${searchTerm}&countryCode=${countryCode}`);
      return response.data.map(InsightsMapper.searchTrendToModel);
    } catch (error) {
      throw new Error('Failed to fetch search trends');
    }
  }

  async getSearchTerms(
    connectorTypes?: string[],
    searchTerm?: string,
    pagination?: PaginationRequest
  ): Promise<PaginatedDTO<Search[]>> {
    try {
      const params: { connectorTypes?: string[]; searchTerm?: string; page?: number; limit?: number } = {};
      if (connectorTypes && connectorTypes.length > 0) {
        params.connectorTypes = connectorTypes;
      }
      if (searchTerm) {
        params.searchTerm = searchTerm;
      }
      if (pagination) {
        params.page = pagination.page;
        params.limit = pagination.limit;
      }
      const response = await this.api.get<PaginatedDTO<SearchDTO[]>>('/insights/search-terms', { params });
      return {
        ...response.data,
        data: response.data.data.map(InsightsMapper.searchToModel)
      };
    } catch (error) {
      throw new Error('Failed to fetch search terms');
    }
  }

  async getSearchTerm(searchTerm: string): Promise<Search> {
    try {
      const encodedSearchTerm = encodeURIComponent(searchTerm);
      const response = await this.api.get<SearchDTO>(`/insights/search-term/${encodedSearchTerm}`);
      return InsightsMapper.searchToModel(response.data);
    } catch (error) {
      throw new Error('Failed to fetch search term');
    }
  }

  async getRankById(rankId: string): Promise<Rank> {
    try {
      const response = await this.api.get<RankDTO>(`/insights/ranks/${rankId}`);
      return InsightsMapper.rankToModel(response.data);
    } catch (error) {
      throw new Error('Failed to fetch rank');
    }
  }
} 